import React, { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { Play, Pause, Download } from "lucide-react";
import { downloadFile } from "../../lib/downloadFile";

function formatTime(sec) {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function AudioPlayer({ src, name, downloadable = true }) {
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: "#3a4670",
      progressColor: "#f5c14b",
      cursorColor: "#f5c14b",
      barWidth: 2,
      barRadius: 2,
      barGap: 2,
      height: 56,
      normalize: true,
      url: src,
    });
    wsRef.current = ws;
    ws.on("ready", () => {
      setDuration(ws.getDuration());
      setReady(true);
    });
    ws.on("audioprocess", () => setCurrent(ws.getCurrentTime()));
    ws.on("seeking", () => setCurrent(ws.getCurrentTime()));
    ws.on("finish", () => setPlaying(false));
    return () => ws.destroy();
  }, [src]);

  const toggle = () => {
    if (!wsRef.current) return;
    wsRef.current.playPause();
    setPlaying((p) => !p);
  };

  return (
    <div
      data-testid="voice-attachment-player"
      className="bg-[#161a2c] border border-white/5 rounded-xl p-4 flex items-center gap-4"
    >
      <button
        onClick={toggle}
        disabled={!ready}
        className="flex-shrink-0 w-11 h-11 rounded-full bg-paul-yellow text-navy flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50"
        style={{ color: "#0b1535" }}
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-white/60 truncate mb-1">{name}</div>
        <div ref={containerRef} className="w-full" />
        <div className="flex justify-between text-[11px] text-white/50 mt-1 font-mono">
          <span>{formatTime(current)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
      {downloadable && (
        <button
          type="button"
          onClick={() => downloadFile(src, name)}
          data-testid="audio-download-btn"
          className="flex-shrink-0 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-paul-yellow hover:border-paul-yellow transition-colors"
          aria-label="Download"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
